import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { Switch } from '../Switch/Switch';

import type { IArtifactSharePannelProps } from './ArtifactSharePannel.type';
import { formatArtifactTitle } from '../../utils/parseArtifacts';

export const ArtifactSharePannel: React.FC<IArtifactSharePannelProps> = ({
  selectedArtifact,
  isOpen,
  isClosing,
  onClose,
  onSave,
}) => {
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(selectedArtifact.readonly_sharable_link_enabled || false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { projectId } = useParams<{ projectId: string }>();

  const handleCopyLink = async () => {
    
    try {
      // Construct the shareable link URL
      const currentHost = window.location.origin;
      const shareableLink = `${currentHost}/project/${projectId}/artifact/${selectedArtifact.identifier}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shareableLink);
      
      // Show success indication
      setLinkCopied(true);
      
      // Reset the indication after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
      
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link to clipboard. Please try again.');
    }
  };

  const handleShareableLinkToggle = async (enabled: boolean) => {
    if (!selectedArtifact?.identifier) return;

    setShareableLinkEnabled(enabled);
    try {
        setIsSaving(true);
        await onSave({
            ...selectedArtifact,
            readonly_sharable_link_enabled: enabled,
        });
    } catch (error) {
      console.error('Failed to update shareable link setting:', error);
      setShareableLinkEnabled(!enabled);
      alert('Failed to update shareable link setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-end bg-alpha-black-70 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="manage-collaboration-title"
    >
      {/* Slide-out Panel */}
      <div
        className={`bg-bg-primary h-full flex flex-col w-full max-w-[500px] shadow-bg-v2-2xl border-l border-border-secondary overflow-y-auto ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-bg-primary flex gap-bg-v2-md items-start p-bg-v2-3xl border-b border-border-secondary shrink-0">
          <div className="flex-1 flex flex-col gap-bg-v2-xxs">
            <h2 id="manage-collaboration-title">
              <Text
                variant="text-lg"
                weight="semibold"
                color="primary"
                className="font-bg-v2-sans"
              >
                Manage Share Artifact
              </Text>
            </h2>
            <Text
              variant="text-sm"
              weight="regular"
              color="secondary"
              className="font-bg-v2-sans"
            >
              Enable or disable the shareable link for this artifact.
            </Text>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            aria-label="Close modal"
          >
            <Icon name={IconName.XClose} size="md" inheritColor />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-xl items-start px-bg-v2-xl py-0 flex-1">
          {/* Divider */}
          <div className="bg-border-secondary h-px w-full" />

          {/* Artifact Name Section */}
          <div className="flex flex-col gap-bg-v2-md items-start px-bg-v2-md py-0 w-full">
            <div className="flex items-center w-full">
              <Text
                variant="text-lg"
                weight="semibold"
                color="primary"
                className="flex-1 font-bg-v2-sans"
              >
                {formatArtifactTitle(selectedArtifact.title, selectedArtifact.version)}
              </Text>
            </div>
          </div>

          {/* Shareable Link Access Section */}
          <div className="bg-bg-primary border border-border-secondary rounded-bg-v2-xl p-bg-v2-2xl flex flex-col gap-bg-v2-xl w-full">
            <div className="flex items-start justify-between w-full gap-bg-v2-xl">
              <div className="flex flex-col gap-bg-v2-xs flex-1">
                <Text
                  variant="text-md"
                  weight="semibold"
                  color="primary"
                  className="font-bg-v2-sans"
                >
                  Shareable Link Access
                </Text>
                <Text
                  variant="text-sm"
                  weight="regular"
                  color="tertiary"
                  className="font-bg-v2-sans"
                >
                  Anyone with the link can access to this artifact
                </Text>
              </div>
              <div className="flex items-center gap-bg-v2-lg shrink-0">
                <Text
                  variant="text-sm"
                  weight="semibold"
                  color={shareableLinkEnabled ? "brand-primary" : "tertiary"}
                  className="font-bg-v2-sans"
                >
                  {shareableLinkEnabled ? 'On' : 'Off'}
                </Text>
                <Switch
                  checked={shareableLinkEnabled}
                  onChange={(e) => handleShareableLinkToggle(e.target.checked)}
                  size="md"
                />
              </div>
            </div>
            
            {shareableLinkEnabled && (
              <>
                <div className="bg-border-secondary h-px w-full" />
                <div className="flex flex-col gap-bg-v2-sm w-full">
                  <div className="relative w-full flex items-center bg-bg-primary-alt border border-border-primary rounded-bg-v2-md shadow-bg-v2-xs">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/project/${projectId}/artifact/${selectedArtifact.identifier}`}
                      className="flex-1 bg-transparent px-bg-v2-lg py-bg-v2-md font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-secondary truncate cursor-default focus:outline-none border-none outline-none"
                      tabIndex={-1}
                    />
                    <button
                    
                      onClick={handleCopyLink}
                      className={`flex items-center justify-center p-2 mr-2 rounded-bg-v2-md transition-all duration-200 shrink-0 ${
                        linkCopied 
                          ? 'bg-bg-brand-primary-alt text-text-brand-primary' 
                          : 'hover:bg-bg-primary-hover text-text-secondary hover:text-text-primary'
                      }`}
                      aria-label="Copy link"
                      type="button"
                    >
                      <Icon 
                        name={linkCopied ? IconName.Check : IconName.Copy} 
                        size="md" 
                        inheritColor 
                      />
                    </button>
                  </div>
                  {linkCopied && (
                    <Text
                      variant="text-xs"
                      weight="medium"
                      color="brand-primary"
                      className="font-bg-v2-sans"
                    >
                      ✓ Link copied to clipboard
                    </Text>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};
